<template>
  <ClientOnly>
    <Teleport to="body">
      <div v-if="hasTarget" class="fixed-right-bar-controls">
        <button
          v-if="!isExpanded"
          type="button"
          class="fixed-right-bar-controls__expand"
          :style="expandPosition"
          :disabled="isAnimating"
          :aria-expanded="isExpanded"
          :aria-label="t('floatingMenu.expandToolbar')"
          :title="t('floatingMenu.expandToolbar')"
          @click="expandBar"
        >
          <img :src="expandIcon" alt="" aria-hidden="true" />
          <span>{{ t('floatingMenu.expand') }}</span>
        </button>

        <Transition name="fixed-right-bar-top-fade">
          <button
            v-if="showScrollTop"
            type="button"
            class="fixed-right-bar-controls__top"
            :style="scrollTopPosition"
            :aria-label="t('floatingMenu.backToTop')"
            :title="t('floatingMenu.backToTop')"
            @click="scrollToTop"
          >
            <img :src="backToTopIcon" alt="" aria-hidden="true" />
          </button>
        </Transition>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import expandIcon from '~/assets/theme/dhf/floating-menu/expand.svg'
import backToTopIcon from '~/assets/theme/dhf/floating-menu/back-to-top.svg'

const TARGET_ID = 'fixed-right-bar-box'
const { t } = useI18n()
const hasTarget = ref(false)
const isExpanded = ref(true)
const hasScrolledOneScreen = ref(false)
const isScrollingToTop = ref(false)
const isAnimating = ref(false)
const railBounds = ref({ top: 0, right: 0, height: 0 })
const expandPosition = computed(() => ({
  top: `${railBounds.value.top + Math.max(railBounds.value.height - 64, 0)}px`,
  right: `${railBounds.value.right}px`,
}))
const scrollTopPosition = computed(() => ({
  top: `${railBounds.value.top + railBounds.value.height + 16}px`,
  right: `${railBounds.value.right}px`,
}))

const showScrollTop = computed(() => (
  hasScrolledOneScreen.value || isScrollingToTop.value
))

let targetElement = null
let targetObserver = null
let targetResizeObserver = null
let syncFrame = 0
let scrollAnimationFrame = 0
let railAnimation = null

function updateScrollTopVisibility() {
  const scrollTop = window.scrollY
    || document.documentElement.scrollTop
    || document.body.scrollTop
    || 0
  hasScrolledOneScreen.value = scrollTop > window.innerHeight
}

function readExpandedState() {
  if (!targetElement) return
  const targetStyle = window.getComputedStyle(targetElement)
  if (!isAnimating.value) {
    isExpanded.value = targetStyle.display !== 'none' && targetStyle.visibility !== 'hidden'
  }
  if (isExpanded.value) {
    const bounds = targetElement.getBoundingClientRect()
    railBounds.value = {
      top: bounds.top,
      right: document.documentElement.clientWidth - bounds.right,
      height: bounds.height,
    }
  }
}

function bindTarget(nextTarget) {
  if (nextTarget === targetElement) {
    readExpandedState()
    return
  }

  targetObserver?.disconnect()
  targetResizeObserver?.disconnect()
  targetElement?.removeEventListener('click', handleTargetClick, true)
  railAnimation?.cancel()
  railAnimation = null
  targetElement = nextTarget
  hasTarget.value = Boolean(targetElement)

  if (!targetElement) return

  targetObserver = new MutationObserver(readExpandedState)
  targetObserver.observe(targetElement, {
    attributes: true,
    attributeFilter: ['class', 'style'],
  })
  targetResizeObserver = new ResizeObserver(readExpandedState)
  targetResizeObserver.observe(targetElement)
  targetElement.addEventListener('click', handleTargetClick, true)
  readExpandedState()
}

function handleTargetClick(event) {
  const collapseTrigger = event.target?.closest?.(
    'a[href*="fixed-right-bar-box"][href*="hide"]',
  )
  if (!collapseTrigger || isAnimating.value) return

  event.preventDefault()
  event.stopImmediatePropagation()
  collapseBar()
}

function collapseBar() {
  if (!targetElement || !isExpanded.value || isAnimating.value) return

  readExpandedState()
  const distance = railBounds.value.right + targetElement.getBoundingClientRect().width
  isAnimating.value = true
  railAnimation?.cancel()
  railAnimation = targetElement.animate(
    [
      { transform: 'translateX(0)', opacity: 1 },
      { transform: `translateX(${distance}px)`, opacity: 0 },
    ],
    { duration: 300, easing: 'ease', fill: 'forwards' },
  )
  railAnimation.onfinish = () => {
    targetElement.style.visibility = 'hidden'
    isExpanded.value = false
    isAnimating.value = false
  }
}

function scheduleTargetSync() {
  window.cancelAnimationFrame(syncFrame)
  syncFrame = window.requestAnimationFrame(() => {
    bindTarget(document.getElementById(TARGET_ID))
  })
}

function expandBar() {
  const nextTarget = document.getElementById(TARGET_ID)
  if (!nextTarget || isAnimating.value) return

  bindTarget(nextTarget)
  targetElement.style.removeProperty('display')
  targetElement.style.visibility = 'visible'
  isExpanded.value = true
  isAnimating.value = true
  const distance = railBounds.value.right + targetElement.getBoundingClientRect().width
  railAnimation?.cancel()
  railAnimation = targetElement.animate(
    [
      { transform: `translateX(${distance}px)`, opacity: 0 },
      { transform: 'translateX(0)', opacity: 1 },
    ],
    { duration: 300, easing: 'ease' },
  )
  railAnimation.onfinish = () => {
    targetElement.style.removeProperty('visibility')
    isAnimating.value = false
    railAnimation = null
  }
}

function scrollToTop() {
  const startTop = window.scrollY
    || document.documentElement.scrollTop
    || document.body.scrollTop
    || 0
  if (isScrollingToTop.value || startTop <= 0) return

  const duration = 300
  const startTime = performance.now()
  isScrollingToTop.value = true

  const scrollStep = (currentTime) => {
    const progress = Math.min((currentTime - startTime) / duration, 1)
    const easedProgress = 1 - (1 - progress) ** 3
    window.scrollTo(0, Math.round(startTop * (1 - easedProgress)))

    if (progress < 1) {
      scrollAnimationFrame = window.requestAnimationFrame(scrollStep)
      return
    }

    isScrollingToTop.value = false
    updateScrollTopVisibility()
  }

  scrollAnimationFrame = window.requestAnimationFrame(scrollStep)
}

onMounted(() => {
  scheduleTargetSync()
  updateScrollTopVisibility()
  window.addEventListener('scroll', updateScrollTopVisibility, { passive: true })
  window.addEventListener('resize', handleResize, { passive: true })
})

function handleResize() {
  updateScrollTopVisibility()
  readExpandedState()
}

onBeforeUnmount(() => {
  window.cancelAnimationFrame(syncFrame)
  window.cancelAnimationFrame(scrollAnimationFrame)
  railAnimation?.cancel()
  targetObserver?.disconnect()
  targetResizeObserver?.disconnect()
  targetElement?.removeEventListener('click', handleTargetClick, true)
  window.removeEventListener('scroll', updateScrollTopVisibility)
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.fixed-right-bar-controls__expand,
.fixed-right-bar-controls__top {
  position: fixed;
  z-index: 2147483000;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  box-sizing: border-box;
  color: #18181b;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
}

.fixed-right-bar-controls__expand {
  width: 48px;
  height: 64px;
  padding: 10px 6px;
  flex-direction: column;
  gap: 4px;
}

.fixed-right-bar-controls__top {
  width: 48px;
  height: 48px;
  padding: 10px 6px 14px;
}

.fixed-right-bar-controls img {
  display: block;
  width: 24px;
  height: 24px;
  flex: none;
}

.fixed-right-bar-controls__expand span {
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
}

.fixed-right-bar-controls__expand:focus-visible,
.fixed-right-bar-controls__top:focus-visible {
  outline: 2px solid var(--el-color-primary-light-7, #ffddc2);
  outline-offset: 2px;
}

.fixed-right-bar-controls__expand:disabled {
  cursor: default;
}

.fixed-right-bar-top-fade-enter-active,
.fixed-right-bar-top-fade-leave-active {
  transition: opacity 200ms ease;
}

.fixed-right-bar-top-fade-enter-from,
.fixed-right-bar-top-fade-leave-to {
  opacity: 0;
}
</style>
